import { isFunction, isPromiseLike } from '../typeguards.js';

/**
 * Represents an observer that can receive notifications from an Observable.
 *
 * @see https://wicg.github.io/observable/
 */
export interface Observer<T> {
    /**
     * Receives the next value in the sequence.
     *
     * @param value The next value in the sequence.
     */
    next(value: T): void;
    /**
     * Receives an error from the Observable.
     *
     * @param error The error that occurred.
     */
    error(error: any): void;
    /**
     * Receives a completion signal from the Observable.
     */
    complete(): void;
}

/**
 * Defines options that can be passed when subscribing to an Observable.
 *
 * @see https://wicg.github.io/observable/
 */
export interface SubscriptionOptions {
    /**
     * A signal that can be used to cancel the subscription.
     */
    signal?: AbortSignal;
}

/**
 * Represents a value that can be converted to an Observable.
 */
export type ConvertableToObservable<T> = Observable<T> | PromiseLike<T> | Iterable<T> | AsyncIterable<T>;

/**
 * A no-operation function used as a default handler.
 */
const NOOP = () => { };

/**
 * Represents a stream of values that can be observed.
 *
 * @see https://wicg.github.io/observable/
 */
export class Observable<T> {

    /**
     * Creates an Observable from various types of data sources, including other Observables, Promises,
     * Iterables (synchronous), and AsyncIterables (asynchronous).
     *
     * @param value The data source to convert to an Observable.
     * @returns An Observable that emits the values from the data source.
     */
    static from<T>(value: ConvertableToObservable<T>): Observable<T> {
        if (value instanceof Observable) {
            return value;
        }
        if (isPromiseLike(value)) {
            return new Observable((subscriber) => {
                value.then(
                    (value) => {
                        subscriber.next(value);
                        subscriber.complete();
                    },
                    (error) => {
                        subscriber.error(error);
                    },
                );
            });
        }
        if (Symbol.asyncIterator in value) {
            return new Observable(async (subscriber) => {
                const iterator = value[Symbol.asyncIterator]();
                try {
                    while (subscriber.isActive) {
                        const { value, done } = await iterator.next();
                        if (done) {
                            subscriber.complete();
                            return;
                        }
                        subscriber.next(value);
                    }
                } catch (error) {
                    subscriber.error(error);
                } finally {
                    iterator.return?.();
                }
            });
        }
        if (Symbol.iterator in value) {
            return new Observable((subscriber) => {
                const iterator = value[Symbol.iterator]();
                try {
                    while (subscriber.isActive) {
                        const { value, done } = iterator.next();
                        if (done) {
                            subscriber.complete();
                            return;
                        }
                        subscriber.next(value);
                    }
                } catch (error) {
                    subscriber.error(error);
                } finally {
                    iterator.return?.();
                }
            });
        }
        throw new TypeError('Value is not observable');
    }

    /**
     * Initializes a new instance of the Observable class, taking a subscriber function
     * that gets called when the Observable is subscribed to.
     *
     * @param start A function that is called when the Observable is subscribed to, receiving a Subscriber<T> to emit values, errors, or completion.
     */
    constructor(
        private start: (subscriber: Subscriber<T>) => void
    ) { }

    /**
     * Subscribes to the Observable to start receiving notifications.
     *
     * @param fnOrObserver a function or observer object to receive notifications.
     * @param options subscription options
     */
    subscribe(onNext: (value: T) => void, options?: SubscriptionOptions): void;
    subscribe(observer: Partial<Observer<T>>, options?: SubscriptionOptions): void
    subscribe(fnOrObserver: Partial<Observer<T>> | ((value: T) => void), options?: SubscriptionOptions): void {
        const partialObserver = isFunction(fnOrObserver) ? { next: fnOrObserver } : fnOrObserver;
        const observer = { next: NOOP, error: globalThis?.reportError ?? console.error, complete: NOOP, ...partialObserver };
        const subscriber = new Subscriber(observer, options?.signal);
        this.start(subscriber);
    }

    /**
     * Processes each value emitted by the Observable with the provided callback function.
     * Returns a Promise that resolves when the Observable completes, or rejects if the Observable emits an error.
     *
     * @param callback A function to process each value emitted by the Observable.
     * @param options Optional. Subscription options, including an AbortSignal.
     * @returns A Promise that resolves when the Observable completes, or rejects on error.
     */
    forEach(callback: (value: T) => void, options?: SubscriptionOptions): Promise<void> {
        return new Promise((resolve, reject) => {
            const ac = new AbortController();
            const signal = options?.signal ? abortSignalAny([ac.signal, options.signal]) : ac.signal;
            this.subscribe(
                {
                    next: (value) => {
                        try {
                            callback(value);
                        } catch (error) {
                            reject(error);
                            ac.abort();
                        }
                    },
                    error: reject,
                    complete: resolve,
                },
                {
                    signal,
                }
            );
        });
    }

    /**
     * Transforms the values emitted by the Observable using the provided projection function and emits the result.
     *
     * @param project A function to transform each value emitted by the Observable.
     * @returns A new Observable that emits the transformed values.
     */
    map<R>(project: (value: T, index: number) => R): Observable<R> {
        return new Observable((destination) => {
            let index = 0;
            this.subscribe(
                {
                    next(value) {
                        let result;
                        try {
                            result = project(value, index++);
                        } catch (error) {
                            destination.error(error);
                            return;
                        }
                        destination.next(result);
                    },
                    error(error) {
                        destination.error(error);
                    },
                    complete() {
                        destination.complete();
                    },
                },
                {
                    signal: destination.signal,
                },
            );
        });
    }

    /**
     * Filters values emitted by the Observable, only emitting those for which the provided predicate function returns true.
     *
     * @param predicate A function to evaluate each emitted value, returning true to include the value.
     * @returns A new Observable that emits only the values that match the predicate.
     */
    filter(predicate: (value: T, index: number) => boolean): Observable<T> {
        return new Observable((destination) => {
            let index = 0;
            this.subscribe(
                {
                    next(value) {
                        let result = false;
                        try {
                            result = predicate(value, index++);
                        } catch (error) {
                            destination.error(error);
                            return;
                        }
                        if (result) {
                            destination.next(value);
                        }
                    },
                    error(error) {
                        destination.error(error);
                    },
                    complete() {
                        destination.complete();
                    },
                },
                {
                    signal: destination.signal,
                },
            );
        });
    }

    /**
     * Emits only the first `count` values emitted by the Observable and then completes.
     *
     * @param count The maximum number of values to emit.
     * @returns A new Observable that emits only the first `count` values before completing.
     */
    take(count: number): Observable<T> {
        return new Observable((destination) => {
            let remaining = count;
            this.subscribe(
                {
                    next(value) {
                        remaining--;
                        if (remaining >= 0) {
                            destination.next(value);
                        }
                        if (remaining === 0) {
                            destination.complete();
                        }
                    },
                    error(error) {
                        destination.error(error);
                    },
                    complete() {
                        destination.complete();
                    },
                },
                {
                    signal: destination.signal,
                },
            );
        });
    }

    /**
     * Skips the first `count` values emitted by the Observable, emitting the rest.
     *
     * @param count The number of values to skip before starting to emit.
     * @returns A new Observable that emits values after skipping the first `count` values.
     */
    drop(count: number): Observable<T> {
        return new Observable((destination) => {
            let seen = 0;
            this.subscribe(
                {
                    next(value) {
                        seen++;
                        if (seen > count) {
                            destination.next(value);
                        }
                    },
                    error(error) {
                        destination.error(error);
                    },
                    complete() {
                        destination.complete();
                    },
                },
                {
                    signal: destination.signal,
                },
            );
        });
    }

    /**
     * Projects each source value to an Observable which is merged in the output Observable.
     *
     * @param project A function that transforms each value into an Observable to be merged into the output Observable.
     * @returns A new Observable that emits the values from each transformed Observable.
     */
    flatMap<R>(project: (value: T, index: number) => ConvertableToObservable<R>): Observable<R> {
        return new Observable((destination) => {
            let queue: T[] = [];
            let index = 0;
            let innerAC: AbortController | undefined;
            let outerComplete = false;
            const startInner = (value: T) => {
                innerAC = new AbortController();
                const signal = abortSignalAny([innerAC.signal, destination.signal]);
                let innerObservable;
                try {
                    innerObservable = Observable.from(project(value, index++));
                } catch (error) {
                    destination.error(error);
                    return;
                }
                innerObservable.subscribe(
                    {
                        next(innerValue) {
                            destination.next(innerValue);
                        },
                        error(error) {
                            destination.error(error);
                        },
                        complete() {
                            innerAC = undefined;
                            if (queue.length > 0) {
                                startInner(queue.shift()!);
                            } else if (outerComplete) {
                                destination.complete();
                            }
                        },
                    },
                    {
                        signal,
                    },
                );
            };
            this.subscribe(
                {
                    next(value) {
                        if (innerAC) {
                            queue.push(value);
                        } else {
                            startInner(value);
                        }
                    },
                    error(error) {
                        destination.error(error);
                    },
                    complete() {
                        outerComplete = true;
                        if (queue.length === 0) {
                            destination.complete();
                        }
                    },
                },
                {
                    signal: destination.signal,
                },
            );
        });
    }

    /**
     * Accumulate values over time, emitting each intermediate result.
     *
     * @param reducer A function that takes the current accumulator and the current value, and returns the new accumulator.
     * @param initialValue The initial accumulator value.
     * @param options Optional. Subscription options, including an AbortSignal.
     * @returns An Observable that emits the accumulated value each time the source Observable emits.
     */
    scan<S>(reducer: (accumulated: S, value: T, index: number) => S, initialValue: S, options?: SubscriptionOptions): Observable<S> {
        return new Observable<S>((destination) => {
            let accumulated = initialValue;
            let index = 0;
            this.subscribe(
                {
                    next(value) {
                        try {
                            // apply the reducer function to the current accumulated value and the latest value emitted by the source Observable.
                            accumulated = reducer(accumulated, value, index++);
                            // emit the new accumulated value.
                            destination.next(accumulated);
                        } catch (error) {
                            // if the reducer function throws, notify the destination Observer of the error.
                            destination.error(error);
                            return;
                        }
                    },
                    error(error) {
                        // forward any errors from the source Observable to the destination.
                        destination.error(error);
                    },
                    complete() {
                        // notify the destination Observer that the source Observable has completed.
                        destination.complete();
                    },
                },
                // use the provided options, including any AbortSignal, for the subscription to the source Observable.
                options,
            );
        });
    }

    /**
     * Projects each source value to an Observable which is merged into the output Observable,
     * emitting values only from the most recently projected Observable.
     *
     * This operator is useful for handling use cases like cancelling ongoing asynchronous operations
     * if a new value is emitted from the source Observable. For instance, it can be used to handle
     * scenarios where a user's action triggers an HTTP request, but you only care about the response
     * of the most recent request.
     *
     * @param project A function that, for each item emitted by the source Observable, returns an Observable.
     * @returns An Observable that emits the items from the most recently projected Observable.
     */
    switchMap<R>(project: (value: T, index: number) => ConvertableToObservable<R>): Observable<R> {
        return new Observable((destination) => {
            // keeps track of the source emission count to provide index to the project function
            let index = 0;
            // flag to track if the source Observable has completed
            let outerComplete = false;
            // used for aborting the current inner Observable subscription
            let innerAC: AbortController | undefined;
            // subscribe to the source Observable
            this.subscribe(
                {
                    next(value) {
                        // abort the previous inner Observable subscription if it exists
                        innerAC?.abort();
                        // create a new AbortController for the new inner Observable subscription
                        innerAC = new AbortController();
                        // combine the signals to respect both the outer and current inner Observables' cancellations
                        const signal = abortSignalAny([innerAC.signal, destination.signal]);
                        let innerObservable;
                        try {
                            // attempt to project the current value to an Observable
                            innerObservable = Observable.from(project(value, index++));
                        } catch (error) {
                            // if projection fails, emit the error through the destination
                            destination.error(error);
                            return;
                        }
                        // subscribe to the projected Observable
                        innerObservable.subscribe(
                            {
                                next(innerValue) {
                                    // emit values from the most recent inner Observable
                                    destination.next(innerValue);
                                },
                                error(error) {
                                    // forward errors from the inner Observable to the destination
                                    destination.error(error);
                                },
                                complete() {
                                    // cleanup for garbage collection after the inner Observable completes
                                    innerAC = undefined;
                                    // if the source Observable has completed, and there are no more inner subscriptions, complete the destination
                                    if (outerComplete) {
                                        destination.complete();
                                    }
                                },
                            },
                            {
                                signal,
                            },
                        );
                    },
                    error(error) {
                        // forward any errors from the source Observable to the destination
                        destination.error(error);
                    },
                    complete() {
                        // mark the source Observable as complete
                        outerComplete = true;
                        // if there's no active inner Observable, complete the destination
                        if (!innerAC) {
                            destination.complete();
                        }
                    },
                },
                {
                    // respect the destination's abort signal
                    signal: destination.signal,
                },
            );
        });
    }

    /**
     * Takes values from the source Observable until the notifier Observable emits a value or completes.
     *
     * @param notifier An Observable that signals the take operation to complete.
     * @returns A new Observable that emits the values from the source Observable until the notifier Observable emits.
     */
    takeUntil(notifier: ConvertableToObservable<unknown>): Observable<T> {
        return new Observable((destination) => {
            Observable.from(notifier).subscribe(
                {
                    next() {
                        destination.complete();
                    },
                    error(error) {
                        destination.error(error);
                    },
                },
                {
                    signal: destination.signal,
                },
            );
            this.subscribe(destination, { signal: destination.signal });
        });
    }

    /**
     * Returns a Promise that resolves to true if every value emitted by the Observable satisfies the provided predicate.
     *
     * @param predicate A function that evaluates each emitted value.
     * @param options Optional. Subscription options, including an AbortSignal.
     * @returns A Promise that resolves to true if all values satisfy the predicate, otherwise false.
     */
    every(predicate: (value: T, index: number) => boolean, options?: SubscriptionOptions): Promise<boolean> {
        return new Promise((resolve, reject) => {
            const ac = new AbortController();
            const signal = options?.signal ? abortSignalAny([ac.signal, options.signal]) : ac.signal;
            let index = 0;
            this.subscribe(
                {
                    next(value) {
                        let result = false;
                        try {
                            result = predicate(value, index++);
                        } catch (error) {
                            reject(error);
                            ac.abort();
                            return;
                        }
                        if (!result) {
                            resolve(false);
                            ac.abort();
                        }
                    },
                    error: reject,
                    complete() {
                        resolve(true);
                    },
                },
                {
                    signal,
                },
            );
        });
    }

    /**
     * Returns a Promise that resolves to true if some value emitted by the Observable satisfies the provided predicate.
     *
     * @param predicate A function that evaluates each emitted value.
     * @param options Optional. Subscription options, including an AbortSignal.
     * @returns A Promise that resolves to true if any value satisfies the predicate, otherwise false.
     */
    some(predicate: (value: T, index: number) => boolean, options?: SubscriptionOptions): Promise<boolean> {
        return new Promise((resolve, reject) => {
            const ac = new AbortController();
            const signal = options?.signal ? abortSignalAny([ac.signal, options.signal]) : ac.signal;
            let index = 0;
            this.subscribe(
                {
                    next(value) {
                        let result = false;
                        try {
                            result = predicate(value, index++);
                        } catch (error) {
                            reject(error);
                            ac.abort();
                            return;
                        }
                        if (result) {
                            resolve(true);
                            ac.abort();
                        }
                    },
                    error: reject,
                    complete() {
                        resolve(false);
                    },
                },
                {
                    signal,
                },
            );
        });
    }

    /**
     * Returns a Promise that resolves with the first value emitted by the Observable that satisfies the provided predicate.
     *
     * @param predicate A function to evaluate each emitted value.
     * @param options Optional. Subscription options, including an AbortSignal.
     * @returns A Promise that resolves with the found value, or rejects if the value is not found or the Observable errors.
     */
    find(predicate: (value: T, index: number) => boolean, options?: SubscriptionOptions): Promise<T> {
        return new Promise((resolve, reject) => {
            const ac = new AbortController();
            const signal = options?.signal ? abortSignalAny([ac.signal, options.signal]) : ac.signal;
            let index = 0;
            this.subscribe(
                {
                    next(value) {
                        let result = false;
                        try {
                            result = predicate(value, index++);
                        } catch (error) {
                            reject(error);
                            ac.abort();
                            return;
                        }
                        if (result) {
                            resolve(value);
                            ac.abort();
                        }
                    },
                    error: reject,
                    complete() {
                        // TODO: Figure out the proper semantics here.
                        reject(new Error('Value not found'));
                    },
                },
                {
                    signal,
                },
            );
        });
    }

    /**
     * Applies an accumulator function over the source Observable, and returns the accumulated result when the Observable completes, given an optional seed value.
     *
     * @param reducer A function to apply to each value emitted by the Observable.
     * @param initialValue The initial accumulation value.
     * @param options Optional. Subscription options, including an AbortSignal.
     * @returns A Promise that resolves with the final accumulated value.
     */
    reduce<S>(reducer: (accumulated: S, value: T, index: number) => S, initialValue: S, options?: SubscriptionOptions): Promise<S> {
        return new Promise((resolve, reject) => {
            const ac = new AbortController();
            const signal = options?.signal ? abortSignalAny([ac.signal, options.signal]) : ac.signal;
            let state = initialValue;
            let index = 0;
            this.subscribe(
                {
                    next(value) {
                        try {
                            state = reducer(state, value, index++);
                        } catch (error) {
                            reject(error);
                            ac.abort();
                            return;
                        }
                    },
                    error: reject,
                    complete() {
                        resolve(state);
                    },
                },
                {
                    signal,
                },
            );
        });
    }

    /**
     * Collects all values emitted by the Observable into an array and returns a Promise that resolves with this array when the Observable completes.
     *
     * @param options Optional. Subscription options, including an AbortSignal.
     * @returns A Promise that resolves with an array of values emitted by the Observable.
     */
    toArray(options?: SubscriptionOptions): Promise<T[]> {
        return this.reduce<T[]>((arr, value) => arr.concat(value), [], options);
    }

    /**
     * Catches errors on the Observable to be handled by returning a new Observable or throwing.
     *
     * @param handleError A function that handles the error and returns a new Observable.
     * @returns A new Observable that replaces the source Observable in case of an error.
     */
    catch<R>(handleError: (error: unknown) => ConvertableToObservable<R>): Observable<T | R> {
        return new Observable((destination) => {
            this.subscribe(
                {
                    next(value) {
                        destination.next(value);
                    },
                    error(error) {
                        Observable.from(handleError(error)).subscribe(destination, {
                            signal: destination.signal,
                        });
                    },
                    complete() {
                        destination.complete();
                    },
                },
                {
                    signal: destination.signal,
                },
            );
        });
    }

    /**
     * Registers a callback to be executed when the Observable completes or errors.
     *
     * @param onFinalize A function to execute when the Observable finishes.
     * @returns A new Observable that mirrors the source Observable and calls the provided callback on completion or error.
     */
    finally(onFinalize: () => void): Observable<T> {
        return new Observable((destination) => {
            destination.addTeardown(onFinalize);
            this.subscribe(destination, {
                signal: destination.signal,
            });
        });
    }

    /**
     * Returns an AsyncGenerator, making the Observable itself asynchronously iterable.
     * This allows the Observable to be used with for-await-of loops.
     *
     * @returns An asynchronous generator that yields values from the Observable.
     */
    [Symbol.asyncIterator](): AsyncGenerator<T> {
        let ac: AbortController | undefined;
        let deferred: Array<[
            resolve: (value: IteratorResult<T, any> | PromiseLike<IteratorResult<T, any>>) => void,
            reject: (reason?: any) => void,
        ]> = [];
        let buffer: T[] = [];
        let hasError = false;
        let error: any = undefined;
        let isComplete = false;
        return {
            next: () => {
                return new Promise((resolve, reject) => {
                    if (buffer.length > 0) {
                        resolve({ value: buffer.shift()!, done: false });
                        return;
                    }
                    if (hasError) {
                        reject(error);
                        return;
                    }
                    if (isComplete) {
                        resolve({ value: undefined, done: true });
                        return;
                    }
                    if (!ac) {
                        ac = new AbortController();
                        this.subscribe(
                            {
                                next(value) {
                                    if (deferred.length > 0) {
                                        const [resolve] = deferred.shift()!;
                                        resolve({ value, done: false });
                                    } else {
                                        buffer.push(value);
                                    }
                                },
                                error(err) {
                                    if (buffer.length > 0) {
                                        hasError = true;
                                        error = err;
                                    } else {
                                        while (deferred.length > 0) {
                                            const [, reject] = deferred.shift()!;
                                            reject(err);
                                        }
                                    }
                                },
                                complete() { },
                            },
                            {
                                signal: ac.signal,
                            },
                        );
                    }
                    deferred.push([resolve, reject]);
                });
            },
            throw: (err) => {
                return new Promise((_resolve, reject) => {
                    ac?.abort();
                    hasError = true;
                    error = err;
                    for (const [, deferredReject] of deferred) {
                        deferredReject(error);
                    }
                    reject(error);
                });
            },
            return: () => {
                return new Promise((resolve) => {
                    ac?.abort();
                    isComplete = true;
                    for (const [deferredResolve] of deferred) {
                        deferredResolve({ value: undefined, done: true });
                    }
                    resolve({ value: undefined, done: true });
                });
            },
            [Symbol.asyncIterator]() {
                return this;
            },
            async [Symbol.asyncDispose]() {
                // NOOP
            },
        };
    }
}

/**
 * Represents a subscriber that manages the observation of an Observable, including handling of notifications and cleanup.
 *
 * @see https://wicg.github.io/observable/
 */
export class Subscriber<T> implements Observer<T> {

    /**
     * A private boolean flag indicating whether the subscriber is active.
     * It is used to prevent any notifications after the subscriber has been unsubscribed
     * or after an error or complete notification has been received.
     */
    #active = true;

    /**
     * An instance of AbortController used to signal the cancellation of the subscription.
     * This can be used to abort ongoing operations related to the subscription.
     */
    #abortController = new AbortController();

    /**
     * An optional array of teardown functions that are called when the subscription
     * is being terminated, either due to an error, completion, or cancellation.
     * These functions are intended for cleanup and releasing resources.
     */
    #teardowns?: Array<() => void>;

    /**
     * An Observer<T> instance that the Subscriber forwards the notifications to.
     * It represents the actual observer logic provided by the consumer of the Observable.
     */
    _observer: Observer<T>;

    /**
     * Provides access to the subscriber's AbortSignal, allowing external entities to check
     * the cancellation status or listen for cancellation events related to this subscription.
     */
    get signal(): AbortSignal {
        return this.#abortController.signal;
    }

    /**
     * Returns the current active status of the subscriber, which is true if the subscription
     * has not been cancelled, errored, or completed, and false otherwise.
     */
    get isActive(): boolean {
        return this.#active && !this.signal.aborted;
    }

    /**
     * Initializes a new instance of a Subscriber.
     * It takes an Observer<T> instance and an optional AbortSignal to use for cancellation.
     *
     * @param observer the observer to forward notifications to
     * @param signal an optional signal to use for cancellation
     */
    constructor(
        observer: Observer<T>,
        signal?: AbortSignal,
    ) {
        this._observer = observer;
        signal?.addEventListener('abort', () => this.#abortController.abort(), { once: true });
    }

    /**
     * Sends the next value in the sequence to the observer if the subscriber is active.
     * This method is called by the Observable to emit values to the subscriber.
     *
     * @param value The value to send to the observer.
     */
    next(value: T): void {
        if (this.isActive) {
            this._observer.next(value);
        }
    }

    /**
     * Notifies the observer of an error, if the subscriber is active.
     *
     * This method will deactivate the subscriber, preventing any further notifications from being received,
     * and will trigger the abort controller to signal cancellation.
     *
     * This is used to handle error scenarios where the observable sequence cannot continue normally.
     *
     * @param error The error to send to the observer.
     */
    error(error: any): void {
        if (this.isActive) {
            this.#active = false;
            this._observer.error(error);
            this.#abortController.abort();
        }
    }

    /**
     * Notifies the observer that the observable sequence has completed and no further values will be emitted.
     *
     * This method will deactivate the subscriber, preventing any further notifications from being received,
     * and will trigger the abort controller to signal cancellation.
     */
    complete(): void {
        if (this.isActive) {
            this.#active = false;
            this._observer.complete();
            this.#abortController.abort();
        }
    }

    /**
     * A private method that executes all teardown functions stored in #teardowns.
     * It's called when the abort signal is triggered.
     *
     * If any teardown function throws an error, it captures and throws a combined error after attempting all teardowns.
     */
    #teardownHandler = () => {
        const teardowns = Array.from(this.#teardowns ?? []);
        this.#teardowns = undefined;
        const errors: unknown[] = [];
        for (let i = teardowns.length - 1; i >= 0; i--) {
            try {
                teardowns[i]();
            } catch (error: unknown) {
                errors.push(error);
            }
        }
        if (errors.length) {
            throw new Error(`${errors.length} teardowns failed.\n ${errors.join('\n')}}`);
        }
    };

    /**
     * Adds a teardown function to be called upon subscription termination.
     * Teardown functions are useful for cleaning up resources, such as event listeners, open files, or timers.
     *
     * If the subscriber is already inactive, the teardown function is executed immediately.
     *
     * @param teardown The function to call when the subscription is terminated.
     */
    addTeardown(teardown: () => void): void {
        if (this.isActive) {
            if (!this.#teardowns) {
                this.#teardowns = [];
                this.#abortController.signal.addEventListener('abort', this.#teardownHandler, { once: true });
            }
            this.#teardowns.push(teardown);
        } else {
            teardown();
        }
    }
}

/**
 * Combines multiple AbortSignals into a single signal that aborts when any of the input signals abort.
 * If the built-in `AbortSignal.any` method is available, it is used to combine the signals.
 *
 * @param signals An iterable of AbortSignal instances to combine.
 * @returns A single AbortSignal that represents the combined state of the input signals.
 */
export const abortSignalAny = (signals: Iterable<AbortSignal>): AbortSignal => {
    if (isFunction((AbortSignal as any).any)) {
        return (AbortSignal as any).any(signals);
    } else {
        const ac = new AbortController();
        const handleAbort = () => {
            ac.abort();
            for (const signal of signals) {
                signal.removeEventListener('abort', handleAbort);
            }
        };
        for (const signal of signals) {
            signal.addEventListener('abort', handleAbort);
        }
        return ac.signal;
    }
}
